import { Switch } from 'react-router-dom';
import { PendingDataProvider } from '../../providers/PendingData.provider';
import AccountCreation from './AccountCreation/AccountCreationContainer';
import AccountSummaryPage from './AccountSummary/AccountSummaryPage';
import AccountAndBillingContainer from '../AccountAndBilling/AccountAndBillingContainer';
import FundAccount from './FundAccount/FundAccountContainer';
import AutoBalanceTransfer from './AutoBalanceTransfer/AutoBalanceTransferContainer';
import WithdrawAccount from './WithdrawAccount/WithdrawAccountContainer';
import BankAccountTransfer from './BankAccountTransfer/BankAccountTransferContainer';
import CreateAutoBalanceTransferContainer from './AutoBalanceTransfer/CreateAutoBalanceTransfer/CreateAutoBalanceTransferContainer';
import AutoBalanceTransferOverview from './AutoBalanceTransfer/TransferOverviewContainer';
import MyBankAccounts from './MyBankAccounts/BankAccountsContainer';
import MyBankAccountDetails from './MyBankAccounts/Details/BankAccountDetailsContainer';
import { usePermissions } from '../../providers/Permissions.provider';
import ProtectedRoute from '../../utils/ProtectedRoute';
import { useLockedFeatures } from '../../providers/LockedFeatures.provider';
import TransferFundsContainer from '../SubAccounts/TransferFunds/TransferFundsContainer';
import { useClientData } from '../../providers/ClientData.provider';
import { JSX } from 'react';
import { useModuleFeatureFlag } from '@/providers/FeatureFlag.provider';

const Routes = (): JSX.Element => {
  const { permissions } = usePermissions();
  const lockedFeatures = useLockedFeatures();
  const featureFlags = useModuleFeatureFlag('myAccount');
  const {
    response: { SubAccountType },
  } = useClientData();

  return (
    <Switch>
      <ProtectedRoute
        isAllowed={featureFlags.isEnabled && !!permissions.ViewAccountSummary}
        showPermission
        exact
        path="/account-summary"
        component={AccountSummaryPage}
      />
      <ProtectedRoute
        isAllowed={
          featureFlags.isEnabled && !!featureFlags.ViewAccountAndBilling && !!lockedFeatures.AccountAndBillingEnabled
        }
        showPermission
        exact
        path="/account-and-billing"
        component={AccountAndBillingContainer}
      />
      <ProtectedRoute
        isAllowed={
          featureFlags.isEnabled && !!featureFlags.ViewAccountAndBilling && !!lockedFeatures.AccountAndBillingEnabled
        }
        showPermission
        path="/account-and-billing/details/:Key/:Type/:Token"
        exact
      >
        <MyBankAccountDetails AccountListingType="Billing" />
      </ProtectedRoute>
      <ProtectedRoute
        isAllowed={
          featureFlags.isEnabled &&
          !!featureFlags.BankAccountTransfer &&
          !!lockedFeatures.BankAccountTransferEnabled &&
          !!permissions.CreateBankAccountTransfer
        }
        showPermission
        exact
        path="/bank-account-transfer"
        component={BankAccountTransfer}
      />
      <ProtectedRoute
        isAllowed={
          featureFlags.isEnabled && !!featureFlags.OperationalAccounts && !!lockedFeatures.OperationalAccountsEnabled
        }
        showPermission
        exact
        path="/operational-accounts"
        component={MyBankAccounts}
      />
      <ProtectedRoute
        isAllowed={
          featureFlags.isEnabled && !!featureFlags.OperationalAccounts && !!lockedFeatures.OperationalAccountsEnabled
        }
        showPermission
        exact
        path="/operational-accounts/details/:Key/:Type/:Token"
        component={MyBankAccountDetails}
      />
      <ProtectedRoute
        isAllowed={
          !!featureFlags.isEnabled && !!featureFlags.AutoBalanceTransfer && !!lockedFeatures.AutoBalanceTransferEnabled
        }
        showPermission
        exact
        path="/create-auto-balance-transfer"
        component={CreateAutoBalanceTransferContainer}
      />
      <ProtectedRoute
        isAllowed={
          !!featureFlags.isEnabled && !!featureFlags.AutoBalanceTransfer && !!lockedFeatures.AutoBalanceTransferEnabled
        }
        showPermission
        exact
        path="/update-auto-balance-transfer"
        component={CreateAutoBalanceTransferContainer}
      />
      <ProtectedRoute
        isAllowed={!!featureFlags.isEnabled}
        showPermission
        exact
        path="/link-my-account"
        component={AccountCreationWithProver}
      />
      <ProtectedRoute
        isAllowed={
          !!lockedFeatures.AutoBalanceTransferEnabled && featureFlags.isEnabled && !!featureFlags.AutoBalanceTransfer
        }
        showPermission
        exact
        path="/auto-balance-transfer"
        component={AutoBalanceTransfer}
      />
      <ProtectedRoute
        isAllowed={
          !!lockedFeatures.AutoBalanceTransferEnabled && featureFlags.isEnabled && !!featureFlags.AutoBalanceTransfer
        }
        showPermission
        exact
        path="/auto-balance-transfer/overview"
        component={AutoBalanceTransferOverview}
      />
      <ProtectedRoute
        isAllowed={
          !!permissions.CreateFundMyAccountTransactions &&
          featureFlags.isEnabled &&
          !!featureFlags.FundMyAccount &&
          !!lockedFeatures.FundMyAccountEnabled
        }
        showPermission
        exact
        path="/fund-my-account"
        component={SubAccountType === 'franchise' ? TransferFundsContainer : FundAccount}
      />
      <ProtectedRoute
        isAllowed={
          !!permissions.CreateWithdrawMyAccountTransactions &&
          featureFlags.isEnabled &&
          !!featureFlags.WithdrawMyAccount &&
          !!lockedFeatures.WithdrawMyAccountEnabled
        }
        showPermission
        exact
        path="/withdraw-my-account"
        component={WithdrawAccount}
      />
    </Switch>
  );
};

function AccountCreationWithProver(): JSX.Element {
  return (
    <PendingDataProvider>
      <AccountCreation
        onStepChange={() => {}}
        onStepReject={() => {}}
        stepUser={0}
        stepUserChange={false}
        isAllowMove
        isClientControlled
      />
    </PendingDataProvider>
  );
}

export default Routes;
